export const OPEN_CHEST_FRAMES = [
  '/open-crate/DK-Chest-01.png',
  '/open-crate/DK-Chest-02.png',
  '/open-crate/DK-Chest-03.png',
  '/open-crate/DK-Chest-04.png',
];

export const SMOKE_FRAMES = Array.from({ length: 109 - 29 + 1 }, (_, frame) => {
  const frameNumber = String(frame + 29).padStart(5, '0');
  return `/smoke/06%20PH_${frameNumber}.png`;
});

export const SHORT_SMOKE_FRAMES = Array.from({ length: 148 - 85 + 1 }, (_, frame) => {
  const frameNumber = String(frame + 85).padStart(5, '0');
  return `/Shortsmoke2/03-PH_${frameNumber}.png`;
});

export const NEON_FRAMES = Array.from({ length: 193 }, (_, frame) => {
  const frameNumber = String(frame).padStart(5, '0');
  return `/NEW/DKReveal_${frameNumber}.png`;
});

export const REVEAL_AUDIO_ASSETS = [
  '/animationv2/Electric-Logo.mp3',
  '/animationv2/WhipWhirlsWhooshes%20SS029401.wav',
  '/animationv2/Thud%20Impact.wav',
  '/animationv2/Cinematic%20Transition.wav',
  '/animationv2/Flipping%20Poker%20Card%20v1.mp3',
];

/** Default (MLB / Big League Draw) reveal animation video. */
export const DEFAULT_REVEAL_VIDEO_SRC = '/video/Reveal-BigLeagueDraw.mp4';

/** NFL-only reveal animation video — never in the global preload list. */
export const NFL_REVEAL_VIDEO_SRC = '/video/Reveal-gridiron-lowres.mp4';

/** NBA-only (Full Court Draw) reveal animation video — never in the global preload list. */
export const NBA_REVEAL_VIDEO_SRC = '/NBA-Reveal/Reveal-NBA-fullcourt-LR.mp4';

export const REVEAL_VIDEO_ASSETS = [DEFAULT_REVEAL_VIDEO_SRC];

export const REVEAL_STATIC_IMAGE_ASSETS = [
  '/animation/shadow-3.svg',
  '/crate-group2.png',
  '/ball-park-2.png',
  '/default-ad.svg',
  '/Big-League-Draw-Logo-Horizontal_Resized.png',
];

/** NFL-only default reveal background — never in the global preload list. */
export const NFL_REVEAL_BACKGROUND_ASSET = '/NFL-Reveal/AdobeStock_483817817-1.png';

/** NFL-only default main header logo — never in the global preload list. */
export const NFL_MAIN_HEADER_LOGO_ASSET = '/NFL-Reveal/Gridiron-Draw-Logo.png';

/** NBA-only default main header logo — never in the global preload list. */
export const NBA_MAIN_HEADER_LOGO_ASSET = '/NBA-Reveal/Logo.png';

/** NBA-only default reveal background — never in the global preload list. */
export const NBA_REVEAL_BACKGROUND_ASSET = '/NBA-Reveal/NBA-reveal-view-background.png';

/** Default (MLB / Big League Draw) main header logo, part of the global preload list. */
export const DEFAULT_MAIN_HEADER_LOGO_ASSET = '/Big-League-Draw-Logo-Horizontal_Resized.png';

/** Default (MLB / Big League Draw) reveal background, part of the global preload list. */
export const DEFAULT_REVEAL_BACKGROUND_ASSET = '/ball-park-2.png';

export const REVEAL_PRELOAD_ASSETS = [
  ...REVEAL_STATIC_IMAGE_ASSETS,
  ...REVEAL_VIDEO_ASSETS,
  ...OPEN_CHEST_FRAMES,
  ...SMOKE_FRAMES,
  ...SHORT_SMOKE_FRAMES,
  ...NEON_FRAMES,
  ...REVEAL_AUDIO_ASSETS,
];

function normalizeSportCode(sport?: string | null): string {
  return String(sport ?? '')
    .trim()
    .toUpperCase();
}

/** Reveal animation video for the contest sport: Gridiron clip for NFL, Full Court for NBA. */
export function getRevealVideoSrc(sport?: string | null): string {
  const sportCode = normalizeSportCode(sport);
  if (sportCode === 'NFL') return NFL_REVEAL_VIDEO_SRC;
  if (sportCode === 'NBA') return NBA_REVEAL_VIDEO_SRC;
  return DEFAULT_REVEAL_VIDEO_SRC;
}

/**
 * Core reveal animation assets plus any sport-specific backgrounds / video.
 * NFL field art + gridiron video are only included when sport === 'NFL', and the NBA header
 * logo, court background + Full Court video only when sport === 'NBA', so no contest downloads
 * or blocks `isReady` on another league's branding.
 */
export function getRevealPreloadAssets(
  sport?: string | null,
  extraAssets: readonly string[] = []
): string[] {
  const sportCode = normalizeSportCode(sport);
  const assets = [...REVEAL_PRELOAD_ASSETS, ...extraAssets];

  if (sportCode === 'NFL') {
    // NFL uses its own field art + header logo + reveal video; skip the MLB ballpark, Big League
    // Draw logo and BLD video. Preloading the MLB logo on an NFL contest is what let the wrong
    // logo hold the screen for a real HTTP request (DKPLAT-813).
    return [
      ...new Set(
        assets
          .filter(
            url =>
              url &&
              url !== DEFAULT_REVEAL_BACKGROUND_ASSET &&
              url !== DEFAULT_MAIN_HEADER_LOGO_ASSET &&
              url !== DEFAULT_REVEAL_VIDEO_SRC
          )
          .concat(NFL_REVEAL_BACKGROUND_ASSET, NFL_MAIN_HEADER_LOGO_ASSET, NFL_REVEAL_VIDEO_SRC)
      ),
    ];
  }

  if (sportCode === 'NBA') {
    // NBA has its own header logo, court background and Full Court Draw video (DKPLAT-905).
    // Drop all three Big League Draw equivalents so none holds the screen while the NBA
    // assets are fetched cold (DKPLAT-813).
    return [
      ...new Set(
        assets
          .filter(
            url =>
              url &&
              url !== DEFAULT_REVEAL_BACKGROUND_ASSET &&
              url !== DEFAULT_MAIN_HEADER_LOGO_ASSET &&
              url !== DEFAULT_REVEAL_VIDEO_SRC
          )
          .concat(NBA_REVEAL_BACKGROUND_ASSET, NBA_MAIN_HEADER_LOGO_ASSET, NBA_REVEAL_VIDEO_SRC)
      ),
    ];
  }

  return [...new Set(assets.filter(Boolean))];
}
